import { Dayjs } from 'dayjs';
import { DepartmentTiny, Qualification, Specialty, SpecialtyShort } from '../settings';
import { LocationTiny } from '../locations';
import { S } from 'vitest/dist/reporters-BECoY4-b.js';

export const SHIFT_STATUSES = {
  OPENED: 'Opened',
  PENDING: 'Pending',
  ASSIGNED: 'Assigned',
  REASSIGNED: 'Reassigned',
  CANCELED: 'Canceled',
  IN_PROGRESS: 'InProgress',
  UNFILLED: 'Unfilled',
  DELETED: 'Deleted',
  EXPIRED: 'Expired',
  UNASSIGNED: 'Unassigned',
  COMPLETED: 'Completed',
} as const;

export const SHIFT_STATUS_LIST = Object.values(SHIFT_STATUSES);

type ShiftStatusesKey = keyof typeof SHIFT_STATUSES;
export type ShiftStatus = (typeof SHIFT_STATUSES)[ShiftStatusesKey];

type ShiftBase = {
  id: string;
  startDate: string; // ISO stringified Date
  endDate: string; // ISO stringified Date
  timeZone: string;
  status: ShiftStatus;
  isUrgent: boolean;
};

export type Shift = ShiftBase & {
  id: string;
  externalId: string;
  agenciesForShare: {
    id: string; //'staffingpartner-00000000000000000000000000';
    logoURL: string;
    name: string;
    tenantId: string;
  }[];
  assignRequest?: {
    status: 'Pending' | 'Approved' | 'Declined';
    staff: {
      id: string;
      imageUrl: string;
      firstName: string;
      lastName: string;
      isInternal: true;
      agencyId: string; //'agency-00000000000000000000000000';
      agencyName: string;
      orientedRecords: [
        {
          locationId: string; //'location-00000000000000000000000000';
          oriented: 'Yes' | 'No';
        },
      ];
    };
    declineReason: string;
  };
  nurseQualifications: Qualification[];
  otherQualifications: Qualification[];
  staff: ShiftAssignee | null;
  startDate: string; // YYYY-MM-DDTHH:mm:ss:sss
  endDate: string; // YYYY-MM-DDTHH:mm:ss:sss
  timeZone: string;
  staffingType: StaffingType;
  location: LocationTiny;
  department: DepartmentTiny;
  subDepartment: DepartmentTiny | null;
  specialty: SpecialtyShort | null;
  tags: string[];
  notes: string[];
  isUrgent: boolean;
  isShared: boolean;
  status: ShiftStatus;
  canHasMultipleAssignee: boolean;
  cancellingReason: string | null;
  // shiftSeriesId: string | null;
  // shiftRotationId: string | null;
  isArchive: boolean;
  recurrence: 'single' | 'series' | 'rotation';
  recurrenceId: string;
};

export type ShiftList = Shift & {
  // recurrence: 'single' | 'series' | 'rotation';
  // shiftSeriesId: string | null;
  // shiftRotationId: string | null;
  // isArchive: boolean;
};

type ShiftAssignee = {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  imageURL: string;
  nurseQualifications: Qualification[];
};

export type ShiftType = 'day' | 'eve' | 'noc';

export type StaffingType = 'All' | 'Internal' | 'External';

export type SingleShift = {
  numberOfCopies: number;
  assignees: string[];
  staffingType: StaffingType;
  startDate: string;
  durationInSeconds: number;
  timeZone: string;
  staffNurseQualificationFilterIds: string[];
  staffOtherQualificationFilterIds: string[];
  departmentNodeId: string | null;
  subDepartmentNodeId: string | null;
  specialtyId: string | null;
  locationId: string;
  tags: string[];
  note: string;
  isUrgent: boolean;
  isNeedToPublish: boolean;
};

// Shifts starting between 4am and 11:59AM are categorized as day shifts.
// Shifts starting between 12pm and 7:59pm are designated as evening shifts.
// Shifts starting between 8pm and 3:59am are classified as night shifts.
export function getShiftType(date: Dayjs): ShiftType {
  if (date.hour() >= 4 && date.hour() < 12) {
    return 'day';
  }
  if (date.hour() >= 12 && date.hour() < 20) {
    return 'eve';
  }

  return 'noc';
}

export function getTimeByShiftType(shift: string): { startTime: string; duration: string } {
  const shiftType = shift.slice(0, 3);
  const duration = parseInt(shift.slice(3, 5), 10);
  const typeToTime: Record<string, string> = {
    day: '04:00:00',
    eve: '12:00:00',
    noc: '20:00:00',
  };

  return {
    startTime: typeToTime[shiftType],
    duration: `${duration.toString().padStart(2, '0')}:00:00`,
  };
}
export function getShiftTypeByTimeAndDuration(startTime: string, duration: string): string {
  const startHour = parseInt(startTime.slice(0, 2), 10) ?? 0;
  const durationHour = parseInt(duration.slice(0, 2), 10) ?? 0;
  const type = startHour <= 4 ? 'noc' : startHour <= 12 ? 'day' : startHour <= 20 ? 'eve' : 'noc';
  const durationStr = durationHour < 12 ? '8h' : '12h';

  return `${type}${durationStr}`;
}
export function getShiftTypeByTimeRange(startDate: Dayjs, endDate: Dayjs) {
  const [start, end] = startDate.isAfter(endDate) ? [endDate, startDate] : [startDate, endDate];
  const startHour = start.hour();
  const durationHour = end.diff(start, 'hours');
  const type = startHour <= 4 ? 'noc' : startHour <= 12 ? 'day' : startHour <= 20 ? 'eve' : 'noc';
  const durationStr = durationHour < 12 ? '8h' : '12h';

  return {
    type,
    durationStr,
    durationHour,
  };
}

export type DepartmentNode = { id: string; name: string };

export type ShiftOld = {
  id: string;
  shiftStart: Date;
  shiftLength: number;
  shiftEnd: Date;
  // TODO: check Intl when real statuses are available
  recurrence: 'single' | 'series' | 'rotation';
  recurrenceId: string;
  location?: {
    name: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
  };
  agenciesForShare: {
    id: string; //'staffingpartner-00000000000000000000000000';
    logoURL: string;
    name: string;
    tenantId: string;
  }[];
  assignRequest: {
    status: 'Pending' | 'Approved' | 'Declined';
    staff: {
      id: string;
      avatar: string;
      firstName: string;
      lastName: string;
      name: string;
      isInternal: true;
      agencyId: string; //'agency-00000000000000000000000000';
      agencyName: string;
      orientedList: [
        {
          locationId: string; //'location-00000000000000000000000000';
          oriented: boolean;
        },
      ];
    };
    declineReason: string;
  };
  qualifications: Qualification[];
  departmentNode: DepartmentNode;
  specialty: SpecialtyShort | null;
  // TODO: check Intl when real statuses are available
  status: 'assigned' | 'reAssigned' | 'pending' | 'inProgress' | 'completed' | 'canceled' | 'na';
  notes?: string;
  assignee: AssigneeOld | null;
  locationDetails?: {
    department?: string;
    subDepartment?: string;
    primaryPhoneNumber: string;
    secondaryPhoneNumber: string;
  };
  mainScheduler?: {
    name: string;
    jobTitle: string;
    primaryPhoneNumber: string;
    email: string;
  };
  subDepartmentNode: {
    id: string; //'departmentnode-00000000000000000000000000';
    name: string;
  };
  canHasMultipleAssignee: boolean;
  afterHoursScheduler?: {
    name: string;
    jobTitle: string;
    primaryPhoneNumber: string;
    email: string;
  };
  timeZone: string;
  isShared: boolean;
  isUrgent: boolean;
  // shiftSeriesId: string | null;
  // shiftRotationId: string | null;
  isArchive: boolean;
  externalId: string;
  tags: string[];
};

export type RecurrenceListItem = {
  id: string;
  externalId: string;
  startDate: Dayjs;
  endDate: Dayjs;
  assignees: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    avatar: string;
    agency: {
      id: string;
      name: string;
    } | null;
  }[];
  agenciesForShare: {
    id: string; //'staffingpartner-00000000000000000000000000';
    logoURL: string;
    name: string;
    tenantId: string;
  }[];
  recurrence: 'single' | 'series' | 'rotation';
  qualifications: Qualification[];
  department: DepartmentNode;
  specialty: SpecialtyShort | null;
  isShared: boolean;
  allowedMultipleAssignees: boolean | null;
  canAssign: boolean;
  canUnassign: boolean;
  totalShiftCount: number;
  assignedShiftCount: number;
  notes: string[];
  tags: string[];
  isArchive: boolean;
};

export type AssigneeOld = {
  agencyId: string | null;
  agencyName: string | null;
  firstName: string;
  name: string;
  id: string;
  avatar: string;
  imageUrl: string;
  isInternal: boolean;
  lastName: string;
  orientedList: { locationId: string; value: boolean }[];
};
